import { useEffect, useRef, useState } from "react";
import { Link } from "@/components/LocalizedLink";
import { CheckCircle2, XCircle, ArrowRight, Sparkles } from "lucide-react";
import { track } from "@/lib/analytics";
import { useAuth } from "@/hooks/useAuth";
import { SignupNudge } from "@/components/SignupNudge";
import { PREMIUM_CTA } from "@/lib/cta";
import { useTranslation } from "react-i18next";
import { incDailyCount } from "@/lib/usage";

type Q = { q: string; options: string[]; correct: number; explain: string };

const CORRECT = [1, 1, 0, 2, 0];

export function MiniTasteTest({ onDone }: { onDone?: () => void } = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const QUESTIONS: Q[] = (t("miniTest.q", { returnObjects: true }) as any[]).map(
    (item, i) => ({ ...item, correct: CORRECT[i] }),
  );
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [showBonus, setShowBonus] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const q = QUESTIONS[idx];
  const isLast = idx === QUESTIONS.length - 1;
  const done = picked !== null && isLast;
  useEffect(() => {
    if (!done) return;
    onDone?.();
    // Visa resultatet och köpknappen – inte e-postrutan längre ner.
    requestAnimationFrame(() => {
      const el = resultRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: "instant" as ScrollBehavior });
    });
  }, [done, onDone]);

  const pick = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === q.correct) setCorrectCount((c) => c + 1);
    // Snabbtestet räknas mot dagens gratiskvot (5/dag).
    incDailyCount();
    track("Mini Quiz Answered", { index: idx, correct: i === q.correct });
  };

  const next = () => {
    setPicked(null);
    setIdx((i) => i + 1);
  };

  if (done) {
    const finalCorrect = correctCount;
    track("Mini Quiz Completed", { correct: finalCorrect, total: QUESTIONS.length });
    return (
      <div ref={resultRef} className="rounded-2xl border border-border bg-card p-6 shadow-card" style={{ overflowAnchor: "none" }}>
        <div className="inline-flex items-center gap-2 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
          <Sparkles className="h-3.5 w-3.5" /> {t("miniTest.resultBadge")}
        </div>
        <h3 className="mt-3 font-display text-2xl font-bold">
          {t("miniTest.scoreLine", { correct: finalCorrect, total: QUESTIONS.length })}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {finalCorrect === QUESTIONS.length
            ? t("miniTest.allRight")
            : t("miniTest.someWrong")}
        </p>
        <div className="mt-5">
          <Link
            to={PREMIUM_CTA.to}
            onClick={() => track("Pricing Click", { surface: "mini_quiz_done" })}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 shadow-elevated"
          >
            {t("miniTest.unlockAll")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {!user && !showBonus && (
          <button
            type="button"
            onClick={() => { setShowBonus(true); track("Mini Quiz Bonus Opened"); }}
            className="mt-4 block w-full text-center text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground sm:text-left"
          >
            {t("miniTest.bonusLink", { defaultValue: "Inte redo? Lås upp 5 bonusfrågor gratis" })}
          </button>
        )}
        {!user && showBonus && <SignupNudge source="mini-quiz" variant="soft" className="mt-4" />}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="rounded-full bg-secondary px-2 py-0.5">{t("miniTest.badge")}</span>
        <span>{t("miniTest.progress", { n: idx + 1, total: QUESTIONS.length })}</span>
      </div>
      <h3 className="mt-3 font-display text-lg font-semibold">{q.q}</h3>
      <ul className="mt-4 space-y-2 text-sm">
        {q.options.map((o, i) => {
          const isCorrect = i === q.correct;
          const isPicked = picked === i;
          const showState = picked !== null;
          return (
            <li key={o}>
              <button
                onClick={() => pick(i)}
                disabled={picked !== null}
                className={`w-full text-left rounded-xl border-2 px-3 py-2.5 flex items-center gap-3 transition-colors ${
                  showState && isCorrect
                    ? "border-success bg-success/10"
                    : showState && isPicked && !isCorrect
                    ? "border-destructive bg-destructive/10"
                    : "border-border hover:bg-secondary"
                }`}
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-xs">
                  {String.fromCharCode(65 + i)}
                </span>
                <span>{o}</span>
                {showState && isCorrect && <CheckCircle2 className="ml-auto h-4 w-4 text-success" />}
                {showState && isPicked && !isCorrect && <XCircle className="ml-auto h-4 w-4 text-destructive" />}
              </button>
            </li>
          );
        })}
      </ul>
      {picked !== null && (
        <div className="mt-4 rounded-lg bg-secondary p-3 text-xs text-muted-foreground">
          <strong className="text-foreground">{t("miniTest.explanation")}</strong>
          {q.explain}
        </div>
      )}
      {picked !== null && !isLast && (
        <button
          onClick={next}
          className="mt-4 w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t("miniTest.next")} <ArrowRight className="ml-1 inline h-4 w-4" />
        </button>
      )}
    </div>
  );
}
